import { useUploadFile } from 'store/files';
import { useCreateNew, useRemoveNew, useUpdateNew } from 'store/news';
import { useCompanyLanguages } from 'state/Administrators';
import { useSurveyResults } from 'store/surveys';
import { ISurvey, ISurveyGroupAssignment } from 'types/income';
import { api } from 'api/api';
import { Endpoints } from '../../api/constants';
import { useLoading } from '../../utils/Loading';
import surveyResultsImagePath from './survey-results.jpg';
import { SurveyResultsNews } from './SurveyResultsNews';

export const useSurveyNewsGenerator = (surveyId: string) => {
  const requestState = useLoading();
  const { defaultLanguage } = useCompanyLanguages();

  const { data: results } = useSurveyResults(surveyId);

  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: createNew } = useCreateNew();
  const { mutateAsync: updateNew } = useUpdateNew();
  const { mutateAsync: removeNew } = useRemoveNew();

  const generate = async () => {
    requestState.startLoading();
    let createdNewsId = '';

    try {
      // Upload image
      const imageResponse = await fetch(surveyResultsImagePath);
      const imageBlob = await imageResponse.blob();
      const file = new File([imageBlob], 'survey-results.jpg');
      const { data } = await uploadFile({ file });

      // Pull survey data
      const surveyResponse = await api<ISurvey>({
        url: Endpoints.clientAdmin.surveys.byId(surveyId).root,
      });
      const survey = surveyResponse.data;

      const getSurveyTranslations = () => {
        if (!survey.languages.length) {
          throw new Error('Survey translations are missing');
        }
        const defaultLangTranslation = survey.languages.find(
          (translation) => translation.language === defaultLanguage,
        );
        return defaultLangTranslation ?? survey.languages[0];
      };
      const translations = getSurveyTranslations();

      if (!results) {
        throw new Error('Survey results data is missing');
      }

      // Generate news data
      const newsData = new SurveyResultsNews({
        language: defaultLanguage,
        imageId: data.id,
        title: translations.title,
        description: translations.description,
        responses: results.questions,
        respondentsCount: results.completedBy,
      });

      // Create news
      const { data: createdNews } = await createNew({
        name: newsData.name,
      });
      createdNewsId = createdNews.id;

      // Populate generated news data to created news
      const { data: updatedNew } = await updateNew({
        newId: createdNewsId,
        update: {
          name: newsData.name,
          sendMethods: newsData.sendMethods,
          translations: newsData.translations,
        },
      });

      // Set the news recipients the same as in the survey
      const surveyGroups = await api<ISurveyGroupAssignment[]>({
        url: Endpoints.clientAdmin.surveys.byId(surveyId).groupAssignments,
      });
      const assignedGroups = surveyGroups.data.filter(
        (group) => group.isAssigned,
      );
      const newsGroupsAssignmentRequest = assignedGroups.map((group) => ({
        employeeGroupId: group.groupId,
        status: 'on',
      }));
      await api({
        url: Endpoints.clientAdmin.news.byId.assignRecipients.replace(
          ':newId',
          createdNewsId,
        ),
        data: newsGroupsAssignmentRequest,
        method: 'post',
      });

      return updatedNew;
    } catch (e) {
      if (createdNewsId) {
        removeNew({ newId: createdNewsId });
      }
      throw e;
    } finally {
      requestState.stopLoading();
    }
  };

  return { generate, loading: requestState.loading };
};
